/**
 * Iter208.79 — Public "Meridian" Site (Agenzia Immobiliare, modern/tech con mappa).
 *
 * Struttura: pill nav flottante sticky in cima, hero con mappa SVG + glass-card + 3 map-pin
 * dinamici, bento grid 4 tile (big + 2 normali + wide), stats-row ticker, about con giant-num,
 * testimonials carousel, cta-card flottante sopra footer scuro.
 * Pill nav → hamburger su mobile (già gestito dal CSS del template).
 */
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSiteLocation, useSiteNavigate, SitePreviewLink as Link } from "@/lib/previewNav";
import { Editable, Removable } from "@/lib/siteEditing";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MER_CSS = `
.meridian-site {
  --paper:#F4F7F7;--surface:#FFFFFF;--ink:#0B1F2A;--accent:#2E7DFF;--accent-dark:#1D5FD1;
  --mint:#1FAE8B;--line:#DCE4E4;--muted:#5C6B6E;
  margin:0;background:var(--paper);color:var(--ink);
  font-family:'Manrope', -apple-system, sans-serif;
  -webkit-font-smoothing:antialiased;overflow-x:hidden;
}
.meridian-site *{box-sizing:border-box;}
.meridian-site h1,.meridian-site h2,.meridian-site h3{font-family:'Sora','Manrope',sans-serif;font-weight:700;color:var(--ink);text-wrap:balance;margin:0;letter-spacing:-0.01em;}
.meridian-site .mono{font-family:'Roboto Mono',monospace;font-variant-numeric:tabular-nums;}
.meridian-site .eyebrow{font-family:'Roboto Mono';font-size:0.7rem;letter-spacing:0.1em;text-transform:uppercase;color:var(--accent-dark);}
.meridian-site a{color:inherit;}
.meridian-site img{max-width:100%;display:block;}
.meridian-site .wrap{max-width:1240px;margin:0 auto;padding:0 32px;}
.meridian-site section{position:relative;}
.meridian-site .nav-dock{position:sticky;top:18px;z-index:80;display:flex;justify-content:center;padding:0 20px;}
.meridian-site .pill{display:flex;align-items:center;gap:28px;background:rgba(255,255,255,0.78);backdrop-filter:blur(14px);border:1px solid rgba(11,31,42,0.08);border-radius:100px;padding:10px 12px 10px 22px;box-shadow:0 12px 30px -14px rgba(11,31,42,0.2);max-width:920px;width:100%;}
.meridian-site .logo{font-family:'Sora';font-weight:700;font-size:1.06rem;display:flex;align-items:center;gap:7px;white-space:nowrap;text-decoration:none;color:var(--ink);}
.meridian-site .logo .mark{width:9px;height:9px;background:var(--accent);border-radius:50%;display:inline-block;}
.meridian-site .pill-links{display:flex;gap:24px;list-style:none;margin:0;padding:0;flex:1;justify-content:center;}
.meridian-site .pill-links a{text-decoration:none;font-size:0.9rem;font-weight:500;color:var(--ink);}
.meridian-site .pill-links a:hover,.meridian-site .pill-links a.active{color:var(--accent-dark);}
.meridian-site .btn{display:inline-flex;align-items:center;gap:8px;background:var(--ink);color:#fff;padding:11px 20px;border-radius:100px;font-weight:600;font-size:0.86rem;text-decoration:none;border:none;transition:background .2s ease;cursor:pointer;white-space:nowrap;}
.meridian-site .btn:hover{background:var(--accent-dark);}
.meridian-site .navmobile{display:none;background:none;border:none;font-size:1.3rem;cursor:pointer;color:var(--ink);}
.meridian-site .mobile-menu{display:none;position:fixed;top:70px;left:20px;right:20px;background:var(--surface);border:1px solid var(--line);border-radius:16px;padding:16px;z-index:75;box-shadow:0 20px 40px -20px rgba(11,31,42,0.3);}
.meridian-site .mobile-menu.open{display:block;}
.meridian-site .mobile-menu a{display:block;padding:10px 0;text-decoration:none;color:var(--ink);font-weight:500;border-bottom:1px solid var(--line);}
.meridian-site .mobile-menu a:last-child{border-bottom:none;}
.meridian-site .hero{padding:28px 20px 0;}
.meridian-site .hero-inner{max-width:1240px;margin:0 auto;border-radius:28px;overflow:hidden;position:relative;min-height:620px;display:flex;align-items:center;background:#0E2A3B;}
.meridian-site .hero-map{position:absolute;inset:0;}
.meridian-site .hero-map svg{width:100%;height:100%;}
.meridian-site .glass-card{position:relative;z-index:3;margin:60px 0 60px 56px;background:rgba(11,31,42,0.55);backdrop-filter:blur(18px);border:1px solid rgba(255,255,255,0.14);border-radius:20px;padding:44px 40px;max-width:460px;color:#fff;}
.meridian-site .glass-card .eyebrow{color:#8FD9C4;}
.meridian-site .glass-card h1{font-size:clamp(2rem,3.6vw,2.7rem);line-height:1.12;color:#fff;margin:16px 0 16px;}
.meridian-site .glass-card p{font-size:1rem;line-height:1.6;color:#C7D6DC;margin:0 0 28px;}
.meridian-site .glass-fields{display:flex;flex-direction:column;gap:12px;margin-bottom:20px;}
.meridian-site .glass-field{background:rgba(255,255,255,0.08);border:1px solid rgba(255,255,255,0.14);border-radius:12px;padding:12px 16px;}
.meridian-site .glass-field label{display:block;font-family:'Roboto Mono';font-size:0.62rem;letter-spacing:0.08em;text-transform:uppercase;color:#8FD9C4;margin-bottom:4px;}
.meridian-site .glass-field select,.meridian-site .glass-field input{width:100%;background:none;border:none;color:#fff;font-family:'Manrope';font-size:0.94rem;outline:none;}
.meridian-site .glass-card .btn{background:var(--accent);width:100%;justify-content:center;padding:13px;}
.meridian-site .glass-card .btn:hover{background:#4B8FFF;}
.meridian-site .map-pin{position:absolute;z-index:2;}
.meridian-site .pin-tag{background:#fff;color:var(--ink);font-family:'Roboto Mono';font-size:0.72rem;font-weight:500;padding:6px 10px;border-radius:8px;box-shadow:0 8px 18px -8px rgba(0,0,0,0.4);white-space:nowrap;position:relative;}
.meridian-site .pin-tag::after{content:"";position:absolute;bottom:-5px;left:14px;width:10px;height:10px;background:inherit;transform:rotate(45deg);}
.meridian-site .pin-tag.accent{background:var(--accent);color:#fff;}
.meridian-site .pin-tag.mint{background:var(--mint);color:#fff;}
.meridian-site .sec-head{display:flex;justify-content:space-between;align-items:flex-end;gap:24px;margin-bottom:36px;}
.meridian-site .sec-head h2{font-size:clamp(1.7rem,2.6vw,2.3rem);}
.meridian-site .sec-head .view-all{font-family:'Roboto Mono';font-size:0.8rem;text-decoration:none;color:var(--accent-dark);font-weight:500;white-space:nowrap;}
.meridian-site .listings{padding:100px 0 40px;}
.meridian-site .bento{display:grid;grid-template-columns:repeat(4,1fr);grid-auto-rows:180px;gap:18px;}
.meridian-site .tile{position:relative;border-radius:20px;overflow:hidden;border-top-left-radius:6px;background:#0B1F2A;text-decoration:none;color:#fff;display:block;}
.meridian-site .tile.big{grid-column:span 2;grid-row:span 2;}
.meridian-site .tile.wide{grid-column:span 2;}
.meridian-site .tile svg,.meridian-site .tile img{width:100%;height:100%;position:absolute;inset:0;object-fit:cover;}
.meridian-site .tile-scrim{position:absolute;inset:0;background:linear-gradient(180deg, rgba(11,31,42,0) 40%, rgba(11,31,42,0.82) 100%);z-index:1;}
.meridian-site .tile-info{position:absolute;left:0;right:0;bottom:0;padding:18px 20px;color:#fff;z-index:2;}
.meridian-site .tile-price{font-family:'Sora';font-weight:700;font-size:1.2rem;}
.meridian-site .tile.big .tile-price{font-size:1.5rem;}
.meridian-site .tile-meta{font-family:'Roboto Mono';font-size:0.74rem;color:#CFE0E6;margin-top:4px;}
.meridian-site .tile-tag{position:absolute;top:14px;left:14px;z-index:2;background:rgba(255,255,255,0.9);color:var(--ink);font-family:'Roboto Mono';font-size:0.64rem;letter-spacing:0.06em;text-transform:uppercase;padding:5px 10px;border-radius:8px;}
.meridian-site .stats{background:var(--ink);color:#fff;padding:40px 0;margin-top:70px;}
.meridian-site .stats-row{display:flex;justify-content:center;align-items:center;gap:0;flex-wrap:wrap;}
.meridian-site .stats-row .stat{display:flex;align-items:baseline;gap:10px;padding:0 30px;}
.meridian-site .stats-row .stat + .stat{border-left:1px solid rgba(255,255,255,0.16);}
.meridian-site .stats-row .num{font-family:'Sora';font-weight:700;font-size:1.5rem;color:var(--mint);}
.meridian-site .stats-row .lbl{font-family:'Roboto Mono';font-size:0.72rem;color:#B9C7CC;}
.meridian-site .about-preview{padding:110px 0;overflow:hidden;}
.meridian-site .about-grid{display:grid;grid-template-columns:1fr 1fr;gap:60px;align-items:center;position:relative;}
.meridian-site .about-copy{position:relative;z-index:2;}
.meridian-site .giant-num{position:absolute;top:-70px;left:-30px;font-family:'Sora';font-weight:700;font-size:14rem;line-height:1;color:rgba(46,125,255,0.08);z-index:1;user-select:none;pointer-events:none;}
.meridian-site .about-copy p{font-size:1.02rem;line-height:1.7;color:var(--muted);margin:18px 0 26px;max-width:440px;}
.meridian-site .link-arrow{display:inline-flex;align-items:center;gap:8px;text-decoration:none;color:var(--ink);font-weight:600;}
.meridian-site .about-art{aspect-ratio:1/1;border-radius:24px;overflow:hidden;position:relative;background:var(--surface);border:1px solid var(--line);}
.meridian-site .about-art svg{width:100%;height:100%;}
.meridian-site .testimonials{padding:0 0 100px;}
.meridian-site .test-scroll{display:flex;gap:20px;overflow-x:auto;padding-bottom:8px;}
.meridian-site .test-card{flex:0 0 380px;background:var(--surface);border:1px solid var(--line);border-radius:20px;padding:30px;}
.meridian-site .stars{color:#F5A623;font-size:0.9rem;letter-spacing:2px;margin-bottom:14px;}
.meridian-site .test-card p{font-size:1rem;line-height:1.6;color:var(--ink);margin:0 0 20px;}
.meridian-site .test-person{display:flex;align-items:center;gap:12px;}
.meridian-site .avatar{width:38px;height:38px;border-radius:50%;background:var(--accent);flex-shrink:0;display:flex;align-items:center;justify-content:center;color:#fff;font-family:'Sora';font-weight:700;font-size:0.9rem;}
.meridian-site .test-person .name{font-weight:600;font-size:0.9rem;}
.meridian-site .test-person .role{font-family:'Roboto Mono';font-size:0.72rem;color:var(--muted);}
.meridian-site .cta-wrap{padding:0 20px;margin-bottom:-90px;position:relative;z-index:5;}
.meridian-site .cta-card{max-width:1100px;margin:0 auto;background:linear-gradient(135deg,#0E2A3B,#0B1F2A);border-radius:26px;padding:60px 56px;display:flex;justify-content:space-between;align-items:center;gap:32px;flex-wrap:wrap;color:#fff;}
.meridian-site .cta-card h2{color:#fff;font-size:clamp(1.6rem,2.6vw,2.2rem);max-width:440px;}
.meridian-site .cta-card .btn{background:var(--mint);color:var(--ink);}
.meridian-site footer{background:var(--ink);color:#B9C7CC;padding:150px 0 30px;margin-top:0;}
.meridian-site .foot-grid{display:grid;grid-template-columns:1.3fr 1fr 1fr 1fr;gap:40px;padding-bottom:44px;border-bottom:1px solid rgba(255,255,255,0.1);}
.meridian-site .foot-grid h4{font-family:'Roboto Mono';font-size:0.7rem;letter-spacing:0.08em;text-transform:uppercase;color:#fff;margin-bottom:16px;}
.meridian-site .foot-grid ul{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:9px;}
.meridian-site .foot-grid a{text-decoration:none;color:#B9C7CC;font-size:0.9rem;}
.meridian-site .foot-grid a:hover{color:var(--mint);}
.meridian-site .foot-logo{font-family:'Sora';font-weight:700;font-size:1.2rem;color:#fff;margin-bottom:12px;}
.meridian-site .foot-bottom{display:flex;justify-content:space-between;padding-top:22px;font-size:0.76rem;font-family:'Roboto Mono';color:#5C6B6E;}
/* Pagine aggiuntive */
.meridian-site .contact-page,.meridian-site .listing-detail-page,.meridian-site .about-full,.meridian-site .annunci-full{padding:60px 0 40px;}
.meridian-site .contact-grid{display:grid;grid-template-columns:1.1fr 0.9fr;gap:56px;align-items:flex-start;}
.meridian-site .field-row{margin-bottom:18px;}
.meridian-site .field-row label{display:block;font-family:'Roboto Mono';font-size:0.7rem;letter-spacing:0.08em;text-transform:uppercase;color:var(--muted);margin-bottom:8px;}
.meridian-site .field-row input,.meridian-site .field-row textarea{width:100%;background:var(--surface);border:1px solid var(--line);border-radius:12px;padding:12px 14px;font-family:'Manrope';font-size:0.95rem;color:var(--ink);outline:none;transition:border-color .2s;}
.meridian-site .field-row input:focus,.meridian-site .field-row textarea:focus{border-color:var(--accent);}
.meridian-site .field-row textarea{min-height:130px;resize:vertical;}
.meridian-site .contact-side{background:var(--surface);border:1px solid var(--line);border-radius:20px;padding:32px 28px;}
.meridian-site .contact-side h3{margin-bottom:14px;font-size:1.15rem;}
.meridian-site .contact-side .row{display:flex;gap:10px;align-items:flex-start;margin:14px 0;font-size:0.95rem;color:var(--ink);}
.meridian-site .contact-side .row svg{width:16px;height:16px;stroke:var(--accent-dark);fill:none;flex-shrink:0;margin-top:2px;}
.meridian-site .contact-map{margin-top:24px;aspect-ratio:16/10;background:var(--paper);border:1px solid var(--line);border-radius:14px;overflow:hidden;}
.meridian-site .contact-map iframe{width:100%;height:100%;border:none;}
.meridian-site .filters-bar{display:flex;gap:12px;flex-wrap:wrap;margin-bottom:32px;padding:20px;background:var(--surface);border:1px solid var(--line);border-radius:20px;}
.meridian-site .filters-bar select,.meridian-site .filters-bar input{background:var(--paper);border:1px solid var(--line);border-radius:10px;color:var(--ink);padding:10px 12px;font-family:'Manrope';font-size:0.9rem;outline:none;}
.meridian-site .listing-hero{aspect-ratio:16/9;background:var(--surface);border-radius:20px;overflow:hidden;margin-bottom:32px;border:1px solid var(--line);}
.meridian-site .listing-hero svg,.meridian-site .listing-hero img{width:100%;height:100%;object-fit:cover;}
.meridian-site .listing-detail-grid{display:grid;grid-template-columns:1.4fr 1fr;gap:48px;}
.meridian-site .listing-info-panel{background:var(--surface);border:1px solid var(--line);border-radius:20px;padding:28px;position:sticky;top:100px;}
.meridian-site .listing-info-panel .price{font-family:'Sora';font-weight:700;font-size:1.9rem;color:var(--accent-dark);margin-bottom:8px;}
.meridian-site .listing-info-panel .stats-list{display:grid;grid-template-columns:1fr 1fr;gap:12px 24px;margin:20px 0 24px;padding:16px 0;border-top:1px solid var(--line);border-bottom:1px solid var(--line);font-family:'Roboto Mono';font-size:0.82rem;color:var(--ink);}
.meridian-site .listing-info-panel .stats-list .k{font-size:0.66rem;letter-spacing:0.08em;text-transform:uppercase;color:var(--muted);display:block;margin-bottom:4px;}
.meridian-site .back-link{display:inline-flex;align-items:center;gap:8px;font-family:'Roboto Mono';font-size:0.82rem;color:var(--accent-dark);text-decoration:none;margin-bottom:20px;font-weight:600;}
.meridian-site .empty-state{padding:80px 20px;text-align:center;color:var(--muted);font-family:'Roboto Mono';font-size:0.9rem;}
.meridian-site .team-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:24px;margin-top:44px;}
.meridian-site .team-card{background:var(--surface);border:1px solid var(--line);border-radius:20px;padding:24px;}
.meridian-site .team-card .team-avatar{aspect-ratio:1;background:var(--accent);border-radius:16px;margin-bottom:16px;display:flex;align-items:center;justify-content:center;font-family:'Sora';font-size:2.5rem;color:#fff;font-weight:700;}
.meridian-site .team-card h4{font-family:'Sora';font-weight:700;font-size:1.05rem;margin:0 0 4px;}
.meridian-site .team-card .role{font-family:'Roboto Mono';font-size:0.72rem;letter-spacing:0.08em;text-transform:uppercase;color:var(--muted);}
@media (max-width: 980px){
  .meridian-site .pill-links{display:none;}
  .meridian-site .navmobile{display:block;}
  .meridian-site .hero-inner{min-height:auto;padding:20px 0;}
  .meridian-site .glass-card{margin:40px 20px;max-width:none;}
  .meridian-site .map-pin{display:none;}
  .meridian-site .bento{grid-template-columns:1fr 1fr;grid-auto-rows:160px;}
  .meridian-site .about-grid{grid-template-columns:1fr;gap:32px;}
  .meridian-site .giant-num{display:none;}
  .meridian-site .foot-grid{grid-template-columns:1fr 1fr;row-gap:28px;}
  .meridian-site .cta-card{padding:40px 28px;}
  .meridian-site footer{padding-top:120px;}
  .meridian-site .contact-grid,.meridian-site .listing-detail-grid{grid-template-columns:1fr;gap:36px;}
  .meridian-site .listing-info-panel{position:static;}
  .meridian-site .team-grid{grid-template-columns:1fr;}
}
`;

function HeroMapArt() {
  return (
    <svg viewBox="0 0 1240 620" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect width="1240" height="620" fill="#0E2A3B"/>
      <g stroke="#1D4A61" strokeWidth="1.4">
        <path d="M0 120 H1240 M0 260 H1240 M0 400 H1240 M0 520 H1240"/>
        <path d="M180 0 V620 M420 0 V620 M700 0 V620 M960 0 V620"/>
      </g>
      <g fill="#123246">
        <rect x="200" y="140" width="180" height="100"/>
        <rect x="440" y="280" width="220" height="100"/>
        <rect x="720" y="130" width="200" height="110"/>
        <rect x="980" y="300" width="220" height="180"/>
      </g>
    </svg>
  );
}

function TileArt({ variant = 0 }) {
  const bgs = ["#2E7DFF","#1FAE8B","#0E2A3B","#5FA8FF"];
  const bg = bgs[variant % 4];
  return (
    <svg viewBox="0 0 500 500" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect width="500" height="500" fill={bg}/>
      <g fill="#0B1F2A" opacity="0.85">
        <rect x="80" y="260" width="150" height="180"/>
        <rect x="260" y="200" width="180" height="240"/>
      </g>
    </svg>
  );
}

function PillNav({ slug, studio, activePage }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const base = `/site/${slug}`;
  const location = useSiteLocation();
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  const nav = [["", "Home", "home"], ["/annunci", "Annunci", "annunci"], ["/chi-siamo", "Chi siamo", "chi-siamo"], ["/contatti", "Contatti", "contatti"]];
  return (
    <>
      <div className="nav-dock" data-testid="mer-nav-dock">
        <div className="pill">
          <Link to={base} className="logo" data-testid="mer-logo"><span className="mark"/>{studio.name || "Meridian"}</Link>
          <ul className="pill-links">
            {nav.map(([p, label, key]) => (
              <li key={key}><Link to={`${base}${p}`} className={activePage === key ? "active" : ""} data-testid={`mer-nav-${key}`}>{label}</Link></li>
            ))}
          </ul>
          <Link to={`${base}/contatti`} className="btn" data-testid="mer-cta-valuta">Valuta casa</Link>
          <button className="navmobile" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu" data-testid="mer-navmobile">☰</button>
        </div>
      </div>
      <div className={"mobile-menu" + (mobileOpen ? " open" : "")} data-testid="mer-mobile-menu">
        {nav.map(([p, label, key]) => <Link key={key} to={`${base}${p}`}>{label}</Link>)}
      </div>
    </>
  );
}

function MerFooter({ slug, studio }) {
  const base = `/site/${slug}`;
  const year = new Date().getFullYear();
  return (
    <footer data-testid="mer-footer">
      <div className="wrap">
        <div className="foot-grid">
          <div>
            <div className="foot-logo">{studio.name || "Meridian"}</div>
            <p style={{fontSize:"0.88rem",lineHeight:1.6,maxWidth:"260px"}}>Agenzia immobiliare digitale{studio.city ? ` a ${studio.city}` : ""}. Annunci verificati, valutazioni trasparenti.</p>
          </div>
          <div><h4>Naviga</h4><ul><li><Link to={base}>Home</Link></li><li><Link to={`${base}/annunci`}>Annunci</Link></li><li><Link to={`${base}/chi-siamo`}>Chi siamo</Link></li><li><Link to={`${base}/contatti`}>Contatti</Link></li></ul></div>
          <div><h4>Contatti</h4><ul>{studio.phone && <li><a href={`tel:${studio.phone}`}>{studio.phone}</a></li>}{studio.email && <li><a href={`mailto:${studio.email}`}>{studio.email}</a></li>}{studio.address && <li>{studio.address}</li>}</ul></div>
          <div><h4>Seguici</h4><ul><li><a href="#" onClick={(e)=>e.preventDefault()}>Instagram</a></li><li><a href="#" onClick={(e)=>e.preventDefault()}>LinkedIn</a></li></ul></div>
        </div>
        <div className="foot-bottom">
          <span>© {year} {studio.name || "Meridian"}</span>
          {studio.vat && <span>P.IVA {studio.vat}</span>}
        </div>
      </div>
    </footer>
  );
}

function BentoTile({ p, slug, index, size }) {
  const priceFmt = p?.price ? `€ ${Number(p.price).toLocaleString("it-IT")}` : "Prezzo su richiesta";
  const cls = "tile" + (size === "big" ? " big" : size === "wide" ? " wide" : "");
  const meta = [p?.rooms ? `${p.rooms} locali` : null, p?.surface_m2 ? `${p.surface_m2} mq` : null, p?.city || null].filter(Boolean).join(" · ");
  return (
    <Link to={`/site/${slug}/annunci/${p.id}`} className={cls} data-testid={`mer-tile-${p.id}`}>
      {p.cover_url ? <img src={p.cover_url} alt={p.address}/> : <TileArt variant={index}/>}
      <div className="tile-scrim"/>
      <span className="tile-tag">{p.tag || "In vendita"}</span>
      <div className="tile-info">
        <div className="tile-price">{priceFmt}</div>
        <div className="tile-meta">{p.address || "Immobile"}{meta ? " · " + meta : ""}</div>
      </div>
    </Link>
  );
}

function ContactFormMer({ slug, propertyId, testIdPrefix = "mer-contact" }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) { toast.error("Nome, email e messaggio obbligatori."); return; }
    setSending(true);
    try {
      await axios.post(`${API}/public/site/${slug}/contact`, { ...form, property_id: propertyId || null });
      setSent(true); setForm({ name:"",email:"",phone:"",message:"" });
      toast.success("Richiesta ricevuta — ti risponderemo entro un giorno lavorativo.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invio non riuscito.");
    } finally { setSending(false); }
  };
  if (sent) return <div className="contact-side" data-testid={`${testIdPrefix}-success`} style={{background:"var(--ink)",color:"#fff"}}><h3 style={{color:"#fff"}}>Richiesta ricevuta.</h3><p style={{color:"#B9C7CC",fontSize:"0.95rem"}}>Ti contatteremo entro un giorno lavorativo.</p></div>;
  return (
    <form onSubmit={submit} data-testid={`${testIdPrefix}-form`}>
      <div className="field-row"><label>Nome e cognome</label><input value={form.name} onChange={(e)=>setForm(f=>({...f, name:e.target.value}))} data-testid={`${testIdPrefix}-name`} required/></div>
      <div className="field-row"><label>Email</label><input type="email" value={form.email} onChange={(e)=>setForm(f=>({...f, email:e.target.value}))} data-testid={`${testIdPrefix}-email`} required/></div>
      <div className="field-row"><label>Telefono</label><input value={form.phone} onChange={(e)=>setForm(f=>({...f, phone:e.target.value}))} data-testid={`${testIdPrefix}-phone`}/></div>
      <div className="field-row"><label>Messaggio</label><textarea value={form.message} onChange={(e)=>setForm(f=>({...f, message:e.target.value}))} data-testid={`${testIdPrefix}-message`} required/></div>
      <button type="submit" className="btn" disabled={sending} data-testid={`${testIdPrefix}-submit`}>{sending ? "Invio…" : "Invia richiesta"}</button>
    </form>
  );
}

function MerHome({ slug, studio, properties }) {
  // 3 map-pin dinamici dai primi 3 immobili
  const pins = (properties || []).slice(0, 3).map((p, i) => ({
    price: p.price ? `€ ${Math.round(Number(p.price)/1000)}k` : "€ —",
    style: { top: i === 0 ? "36%" : i === 1 ? "58%" : "22%", left: i === 0 ? "58%" : i === 1 ? "70%" : "66%" },
    variant: i === 0 ? "accent" : i === 1 ? "" : "mint",
    id: p.id,
  }));
  // 4 tile del bento (big + 2 normal + wide)
  const bento = (properties || []).slice(0, 4);
  const sizes = ["big", "", "", "wide"];
  return (
    <>
      <Removable sectionKey="hero">
      <section className="hero" data-testid="mer-section-hero">
        <div className="hero-inner">
          <div className="hero-map"><HeroMapArt/></div>
          {pins.map((pin, i) => (
            <span key={i} className="map-pin" style={pin.style} data-testid={`mer-map-pin-${i}`}>
              <span className={"pin-tag " + pin.variant}>{pin.price}</span>
            </span>
          ))}
          <div className="glass-card">
            <span className="eyebrow"><Editable field="mer.hero.eyebrow">Cerca sulla mappa</Editable></span>
            <Editable field="mer.hero.h1" as="h1" multiline>La tua prossima casa, geolocalizzata.</Editable>
            <Editable field="mer.hero.p" as="p" multiline>Annunci verificati, aggiornati ogni giorno, con quartiere e servizi vicini già sulla mappa.</Editable>
            <div className="glass-fields">
              <div className="glass-field"><label>Zona</label><select defaultValue=""><option value="">{studio.city ? `${studio.city} – tutte le zone` : "Tutte le zone"}</option><option>Centro</option><option>Navile</option></select></div>
              <div className="glass-field"><label>Budget</label><select defaultValue=""><option value="">Tutti</option><option>Fino a 300k</option><option>300k – 600k</option><option>600k+</option></select></div>
            </div>
            <Link to={`/site/${slug}/annunci`} className="btn" data-testid="mer-hero-cta-cerca"><Editable field="mer.hero.cta">Cerca immobili →</Editable></Link>
          </div>
        </div>
      </section>
      </Removable>

      {bento.length > 0 && (
        <Removable sectionKey="listings">
        <section className="listings" data-testid="mer-section-listings">
          <div className="wrap">
            <div className="sec-head">
              <div><span className="eyebrow">In evidenza</span><Editable field="mer.listings.h2" as="h2" style={{marginTop:"10px"}}>Annunci selezionati per te</Editable></div>
              <Link to={`/site/${slug}/annunci`} className="view-all">Vedi tutti →</Link>
            </div>
            <div className="bento" data-testid="mer-bento">
              {bento.map((p, i) => <BentoTile key={p.id} p={p} slug={slug} index={i} size={sizes[i]}/>)}
            </div>
          </div>
        </section>
        </Removable>
      )}

      <Removable sectionKey="stats">
      <section className="stats" data-testid="mer-section-stats">
        <div className="wrap">
          <div className="stats-row">
            <div className="stat"><span className="num mono">247</span><span className="lbl">Immobili gestiti</span></div>
            <div className="stat"><span className="num mono">98%</span><span className="lbl">Clienti soddisfatti</span></div>
            <div className="stat"><span className="num mono">3.2gg</span><span className="lbl">Tempo medio risposta</span></div>
            <div className="stat"><span className="num mono">15</span><span className="lbl">Quartieri coperti</span></div>
          </div>
        </div>
      </section>
      </Removable>

      <Removable sectionKey="about-preview">
      <section className="about-preview" data-testid="mer-section-about-preview">
        <div className="wrap">
          <div className="about-grid">
            <div className="about-copy">
              <div className="giant-num" data-testid="mer-giant-num">01</div>
              <span className="eyebrow">Chi siamo</span>
              <Editable field="mer.about.h2" as="h2" multiline style={{fontSize:"clamp(1.7rem,2.6vw,2.4rem)",marginTop:"12px"}}>{`Agenti che parlano poco${studio.city ? ` di ${studio.city}` : " di città"}, ma la conoscono strada per strada.`}</Editable>
              <Editable field="mer.about.p" as="p" multiline>{`${studio.name || "Meridian"} unisce dati e territorio: prezzi trasparenti al metro quadro, mappe aggiornate, un consulente umano che ti risponde entro un giorno.`}</Editable>
              <Link to={`/site/${slug}/chi-siamo`} className="link-arrow">Scopri di più →</Link>
            </div>
            <div className="about-art">
              <svg viewBox="0 0 400 400" preserveAspectRatio="xMidYMid slice">
                <rect width="400" height="400" fill="#2E7DFF"/>
                <g fill="#0B1F2A" opacity="0.85"><circle cx="200" cy="180" r="80"/><path d="M120 340c0-44 36-70 80-70s80 26 80 70"/></g>
              </svg>
            </div>
          </div>
        </div>
      </section>
      </Removable>

      <Removable sectionKey="testimonials">
      <section className="testimonials" data-testid="mer-section-testimonials">
        <div className="wrap">
          <div className="sec-head">
            <div><span className="eyebrow">Cosa dicono</span><Editable field="mer.testimonials.h2" as="h2" style={{marginTop:"10px"}}>Le voci dei nostri clienti</Editable></div>
          </div>
          <div className="test-scroll">
            {[
              { stars:5, quote:"Trovato l'appartamento perfetto in due settimane. Prezzo giusto, consulente presente.", name:"Marta Ricci", role:"Acquirente, "+ (studio.city || "Bologna") },
              { stars:5, quote:"L'unica agenzia che mi ha detto che il mio immobile valeva meno di quanto pensassi. E aveva ragione.", name:"Luca Bianchi", role:"Venditore" },
              { stars:5, quote:"Mappa e prezzi al mq trasparenti fin dal primo contatto. Zero sorprese.", name:"Elena Conti", role:"Acquirente" },
            ].map((t, i) => (
              <div key={i} className="test-card" data-testid={`mer-test-card-${i}`}>
                <div className="stars">{"★".repeat(t.stars)}</div>
                <p>«{t.quote}»</p>
                <div className="test-person"><div className="avatar">{t.name[0]}</div><div><div className="name">{t.name}</div><div className="role">{t.role}</div></div></div>
              </div>
            ))}
          </div>
        </div>
      </section>
      </Removable>

      <Removable sectionKey="cta">
      <div className="cta-wrap" data-testid="mer-section-cta">
        <div className="cta-card">
          <Editable field="mer.cta.h2" as="h2" multiline>Vuoi sapere quanto vale casa tua? Valutazione trasparente in 48 ore.</Editable>
          <Link to={`/site/${slug}/contatti`} className="btn" data-testid="mer-cta-btn"><Editable field="mer.cta.btn">Richiedi valutazione →</Editable></Link>
        </div>
      </div>
      </Removable>
    </>
  );
}

function MerAnnunci({ slug }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState("");
  const [search, setSearch] = useState("");
  useEffect(() => {
    axios.get(`${API}/public/site/${slug}/properties`).then((r) => setItems(r.data.properties || []))
      .catch(() => setItems([])).finally(() => setLoading(false));
  }, [slug]);
  const cities = useMemo(() => Array.from(new Set(items.map(i => i.city).filter(Boolean))), [items]);
  const filtered = useMemo(() => items.filter((p) => {
    if (city && p.city !== city) return false;
    if (search && !(p.address || "").toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [items, city, search]);
  const sizes = ["big","","","wide"];
  return (
    <section className="annunci-full" data-testid="mer-page-annunci">
      <div className="wrap">
        <div className="sec-head">
          <div><span className="eyebrow">Tutti gli immobili</span><h2 style={{marginTop:"10px"}}>Il nostro catalogo</h2></div>
          <span className="view-all" data-testid="mer-annunci-count">{filtered.length} immobil{filtered.length === 1 ? "e" : "i"}</span>
        </div>
        <div className="filters-bar" data-testid="mer-annunci-filters">
          <select value={city} onChange={(e)=>setCity(e.target.value)} data-testid="mer-filter-city">
            <option value="">Tutte le zone</option>
            {cities.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input placeholder="Cerca per indirizzo…" value={search} onChange={(e)=>setSearch(e.target.value)} data-testid="mer-filter-search" style={{flex:1,minWidth:"220px"}}/>
        </div>
        {loading ? <div className="empty-state">Caricamento…</div>
          : filtered.length === 0 ? <div className="empty-state" data-testid="mer-annunci-empty">Nessun immobile disponibile.</div>
          : <div className="bento">{filtered.map((p, i) => <BentoTile key={p.id} p={p} slug={slug} index={i} size={sizes[i % 4]}/>)}</div>}
      </div>
    </section>
  );
}

function MerChiSiamo({ slug, studio }) {
  const team = [
    { name: studio.name || "Meridian", role: "Fondatore", initial: (studio.name || "M")[0] },
    { name: "Andrea Costa", role: "Consulente Senior", initial: "A" },
    { name: "Sofia Neri", role: "Data & Prezzi", initial: "S" },
  ];
  const founded = studio.founded_year;
  return (
    <section className="about-full" data-testid="mer-page-chi-siamo">
      <div className="wrap">
        <span className="eyebrow">Chi siamo</span>
        <h2 style={{fontSize:"clamp(2rem,3.4vw,2.8rem)",marginTop:"12px",marginBottom:"24px"}}>Dati e territorio, insieme.</h2>
        <p style={{fontSize:"1.1rem",lineHeight:1.7,maxWidth:"760px",color:"var(--muted)"}}>
          {studio.name || "Meridian"} nasce{founded ? ` nel ${founded}` : ""}{studio.city ? ` a ${studio.city}` : ""} dall'idea che comprare o vendere casa oggi meriti trasparenza, dati aggiornati e persone che conoscono davvero il quartiere. Prezzi al mq trasparenti, mappe live, un consulente umano.
        </p>
        <span className="eyebrow" style={{marginTop:"56px",display:"block"}}>Il team</span>
        <div className="team-grid">
          {team.map((m, i) => (
            <div className="team-card" key={i} data-testid={`mer-team-${i}`}>
              <div className="team-avatar">{m.initial}</div>
              <h4>{m.name}</h4>
              <div className="role">{m.role}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function MerContatti({ slug, studio }) {
  const mapQuery = encodeURIComponent(studio.address || studio.city || "Bologna");
  return (
    <section className="contact-page" data-testid="mer-page-contatti">
      <div className="wrap">
        <span className="eyebrow">Contattaci</span>
        <h2 style={{fontSize:"clamp(2rem,3.4vw,2.8rem)",marginTop:"12px",marginBottom:"48px"}}>Parliamone.</h2>
        <div className="contact-grid">
          <div><ContactFormMer slug={slug} testIdPrefix="mer-contatti"/></div>
          <aside className="contact-side" data-testid="mer-contatti-side">
            <h3>Recapiti</h3>
            {studio.phone && <div className="row"><svg viewBox="0 0 24 24" strokeWidth="1.6"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z"/></svg>{studio.phone}</div>}
            {studio.email && <div className="row"><svg viewBox="0 0 24 24" strokeWidth="1.6"><path d="M4 4h16v16H4z"/><path d="M4 4l8 8 8-8"/></svg>{studio.email}</div>}
            {studio.address && <div className="row"><svg viewBox="0 0 24 24" strokeWidth="1.6"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>{studio.address}</div>}
            <div className="contact-map" data-testid="mer-contatti-map">
              <iframe title="Mappa" src={`https://www.google.com/maps?q=${mapQuery}&output=embed`} loading="lazy"/>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

function MerListingDetail({ slug, pid }) {
  const [prop, setProp] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/public/site/${slug}/properties/${pid}`)
      .then((r) => setProp(r.data.property)).catch(() => setProp(null)).finally(() => setLoading(false));
  }, [slug, pid]);
  if (loading) return <section className="listing-detail-page"><div className="wrap empty-state">Caricamento…</div></section>;
  if (!prop) return <section className="listing-detail-page"><div className="wrap empty-state" data-testid="mer-listing-detail-not-found">Immobile non disponibile.</div></section>;
  const priceFmt = prop.price ? `€ ${Number(prop.price).toLocaleString("it-IT")}` : "Prezzo su richiesta";
  return (
    <section className="listing-detail-page" data-testid="mer-page-listing-detail">
      <div className="wrap">
        <Link to={`/site/${slug}/annunci`} className="back-link" data-testid="mer-listing-back">← Torna agli annunci</Link>
        <div className="listing-hero">{prop.cover_url ? <img src={prop.cover_url} alt={prop.address}/> : <TileArt variant={0}/>}</div>
        <div className="listing-detail-grid">
          <div>
            <span className="eyebrow">{prop.tag || "In vendita"}</span>
            <h2 style={{fontSize:"clamp(1.7rem,3vw,2.4rem)",marginTop:"12px"}}>{prop.address}</h2>
            <p style={{color:"var(--muted)",fontSize:"1rem",marginTop:"6px"}}>{prop.city}</p>
            {prop.description && <p style={{fontSize:"1rem",lineHeight:1.7,marginTop:"32px"}}>{prop.description}</p>}
          </div>
          <aside className="listing-info-panel" data-testid="mer-listing-info-panel">
            <div className="price">{priceFmt}</div>
            <div className="stats-list">
              {prop.surface_m2 ? <div><span className="k">Superficie</span>{prop.surface_m2} mq</div> : null}
              {prop.rooms ? <div><span className="k">Locali</span>{prop.rooms}</div> : null}
              {prop.bathrooms ? <div><span className="k">Bagni</span>{prop.bathrooms}</div> : null}
              {prop.city ? <div><span className="k">Zona</span>{prop.city}</div> : null}
            </div>
            <h3 style={{fontSize:"1.05rem",marginBottom:"18px"}}>Richiedi info</h3>
            <ContactFormMer slug={slug} propertyId={pid} testIdPrefix="mer-listing-contact"/>
          </aside>
        </div>
      </div>
    </section>
  );
}

// ============================================================================
//  Root
// ============================================================================
export default function MeridianSite({ initialData, slug: slugProp }) {
  const location = useSiteLocation();
  const navigate = useSiteNavigate();
  const slug = slugProp;
  const [data, setData] = useState(initialData || null);
  const [loading, setLoading] = useState(!initialData);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (initialData || !slug) return;
    axios.get(`${API}/public/site/${slug}`)
      .then((r) => setData(r.data)).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [slug, initialData]);

  const subPath = location.pathname.replace(`/site/${slug}`, "").replace(/^\//, "");
  const [seg1, seg2] = subPath.split("/");
  let activePage = "home";
  if (seg1 === "annunci") activePage = "annunci";
  else if (seg1 === "chi-siamo") activePage = "chi-siamo";
  else if (seg1 === "contatti") activePage = "contatti";

  if (loading) return <div className="meridian-site"><style>{MER_CSS}</style><div className="empty-state" style={{padding:"120px 20px"}}>Caricamento…</div></div>;
  if (notFound || !data) return (
    <div className="meridian-site" data-testid="mer-not-found"><style>{MER_CSS}</style>
      <div className="empty-state" style={{padding:"120px 20px"}}>Sito non trovato. <button onClick={() => navigate("/")} style={{background:"none",border:"none",color:"var(--accent-dark)",cursor:"pointer",textDecoration:"underline"}}>Torna alla homepage</button></div>
    </div>
  );

  const { studio, featured_properties: properties } = data;
  return (
    <div className="meridian-site" data-testid="mer-site-root" data-slug={slug}>
      <style>{MER_CSS}</style>
      <link rel="preconnect" href="https://fonts.googleapis.com"/>
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin=""/>
      <link href="https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700&family=Manrope:wght@400;500;600&family=Roboto+Mono:wght@400;500&display=swap" rel="stylesheet"/>
      <PillNav slug={slug} studio={studio} activePage={activePage}/>
      {(!seg1) ? <MerHome slug={slug} studio={studio} properties={properties}/>
        : seg1 === "annunci" && !seg2 ? <MerAnnunci slug={slug}/>
        : seg1 === "annunci" && seg2 ? <MerListingDetail slug={slug} pid={seg2}/>
        : seg1 === "chi-siamo" ? <MerChiSiamo slug={slug} studio={studio}/>
        : seg1 === "contatti" ? <MerContatti slug={slug} studio={studio}/>
        : <div className="empty-state" style={{padding:"120px 20px"}}>Pagina non trovata.</div>}
      <MerFooter slug={slug} studio={studio}/>
    </div>
  );
}
